import React, { forwardRef, CSSProperties, ReactNode } from 'react';
import { Drawer as MuiDrawer, DrawerProps as MuiDrawerProps, CSSObject, Theme, useTheme, styled } from '@mui/material';

export const MAX_DRAWER_WIDTH = 300;
export const MIN_DRAWER_WIDTH = 100;

const openedMixin = (theme: Theme, width: string | number): CSSObject => ({
    width,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme, width: string | number): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `${width}px`,
});

export const BarDrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

export interface DrawerProps extends Omit<MuiDrawerProps, 'children'> {
    children?: ReactNode;
    width?: string | number;
    height?: string | number;
    open?: boolean;
    collapsedWidth?: string | number;
    expandedWidth?: string | number;
    customButton?: ReactNode;
    customPaperProps?: CSSProperties;
    zIndex?: number;
    transparentBackdrop?: boolean;
}

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>((props, ref) => {
    const {
        children,
        width,
        height,
        open,
        collapsedWidth = 101,
        expandedWidth = MAX_DRAWER_WIDTH,
        customButton,
        customPaperProps,
        zIndex = 1200,
        transparentBackdrop = false,
        sx,
        ...muiProps
    } = props;

    const theme = useTheme();
    const isTemporary = muiProps.variant === 'temporary';
    const isCollapsible = open !== undefined && !isTemporary; // Only apply collapsible logic to permanent

    const collapsibleStyles = isCollapsible
        ? {
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            ...(open ? openedMixin(theme, expandedWidth) : closedMixin(theme, collapsedWidth)),
        }
        : {};

    const sizeStyles = !isCollapsible
        ? {
            ...(width && { width }),
            ...(height && { height }),
        }
        : {};

    const paperStyle: CSSProperties = {
        width: isCollapsible
            ? (open ? `${expandedWidth}px` : `${collapsedWidth}px`)
            : (customPaperProps?.width || width || expandedWidth),
        boxSizing: 'border-box',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
        }),
        ...(customPaperProps || {}),
    };

    return (
        <div style={{ position: 'relative', zIndex }}>
            {customButton}
            <MuiDrawer
                ref={ref}
                anchor={muiProps.anchor || 'left'}
                open={open ?? false}
                onClose={muiProps.onClose} // Pass onClose to handle backdrop clicks
                PaperProps={{
                    style: paperStyle,
                }}
                sx={{
                    zIndex,
                    '& .MuiModal-backdrop': {
                        backgroundColor: transparentBackdrop ? 'transparent' : undefined,
                    },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        ...(isCollapsible
                            ? open
                                ? { width: `${expandedWidth}px !important` }
                                : { width: `${collapsedWidth}px !important` }
                            : {}),
                        [theme.breakpoints.up('sm')]: {
                            ...(isCollapsible
                                ? open
                                    ? { width: `${expandedWidth}px !important` }
                                    : { width: `${collapsedWidth}px !important` }
                                : {}),
                        },
                        ...sizeStyles,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
                        }),
                    },
                    ...collapsibleStyles,
                    ...sx,
                }}
                {...muiProps}
            >
                {children}
            </MuiDrawer>
        </div>
    );
});